import { createContext, type PropsWithChildren, useContext } from 'react';

import { REPUBS } from '@cx-modules/constants';

import { type RequestContext } from '~/types/RequestContext';

import { configFactoryMap } from '../configuration/betting-insights';
import { type BettingInsights } from '../configuration/betting-insights/BettingInsightsConfig';
import {
	type BettingInsightsConfigFactoryMap,
	type EsIndex,
} from '../configuration/betting-insights/types';

export const BettingInsightsRepubConfigContext = createContext<{
	configFactoryMap?: BettingInsightsConfigFactoryMap;
	context: RequestContext;
}>({
	context: {
		environment: 'PROD',
		featureFlags: [],
		user: {
			// NOTE: When we go global this might need to become:
			// TZ: 'UTC',
			// locale: 'en-GB',
			TZ: 'America/New_York',
			ipAddress: null,
			locale: REPUBS.US.locale,
			isLoggedIn: false,
			walls: [],
		},
	},
});

export function BettingInsightsConfigProvider({
	children,
	context,
}: PropsWithChildren<{
	context: RequestContext;
}>) {
	return (
		<BettingInsightsRepubConfigContext.Provider
			value={{
				configFactoryMap,
				context,
			}}
		>
			{children}
		</BettingInsightsRepubConfigContext.Provider>
	);
}

export function useBettingInsightsConfig<TIndex extends EsIndex>(
	index: TIndex,
): BettingInsights<TIndex> {
	const { configFactoryMap, context } = useContext(
		BettingInsightsRepubConfigContext,
	);
	if (!configFactoryMap) {
		throw new Error(
			'useBettingInsightsRepubConfig can only be used inside of BettingInsightsRepubConfigProvider',
		);
	}

	return configFactoryMap[index](context);
}
